import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { Partner } from "../../types/landing";
import { uploadService } from "../../services/uploadService";
import { toast } from "react-hot-toast";

interface PartnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  partner: Partner | null;
  mode: "create" | "edit";
}

const PartnerFormModal: React.FC<PartnerFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  partner,
  mode,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    logo: {
      url: "",
      alt: "",
    },
    type: "partner" as "accreditation" | "partner",
    link: "",
    description: "",
    status: "active" as "active" | "inactive",
    displayOrder: 0,
  });

  const [logoPreview, setLogoPreview] = useState("");
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (partner && mode === "edit") {
      setFormData({
        name: partner.name || "",
        logo: {
          url: partner.logo?.url || "",
          alt: partner.logo?.alt || "",
        },
        type: partner.type || "partner",
        link: partner.link || "",
        description: partner.description || "",
        status: partner.status || "active",
        displayOrder: partner.displayOrder || 0,
      });
      setLogoPreview(partner.logo?.url || "");
    } else {
      resetForm();
    }
  }, [partner, mode]);

  const resetForm = () => {
    setFormData({
      name: "",
      logo: {
        url: "",
        alt: "",
      },
      type: "partner",
      link: "",
      description: "",
      status: "active",
      displayOrder: 0,
    });
    setLogoPreview("");
    setSelectedLogoFile(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    if (name.startsWith("logo.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        logo: {
          ...prev.logo,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log("File selected:", file);

    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        e.target.value = ""; // Reset input
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        e.target.value = ""; // Reset input
        return;
      }

      setSelectedLogoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
      console.log("Logo preview set:", previewUrl);
      console.log("Selected file:", file.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);

    try {
      let logoUrl = formData.logo.url;

      // Upload logo if a new file was selected
      if (selectedLogoFile) {
        const uploadedUrl = await uploadService.uploadImage(
          selectedLogoFile,
          "landingpage/partners"
        );
        logoUrl = uploadedUrl;
      }

      const submitData = {
        ...formData,
        logo: {
          url: logoUrl,
          alt: formData.logo.alt || formData.name,
        },
      };

      await onSubmit(submitData);
      resetForm();
    } catch (error) {
      console.error("Error submitting partner:", error);
      toast.error("Failed to save partner");
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "create" ? "Add New Partner" : "Edit Partner"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partner Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter partner name"
              required
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="partner">Partner</option>
              <option value="accreditation">Accreditation</option>
            </select>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              {logoPreview && (
                <div className="relative inline-block">
                  <img
                    src={logoPreview}
                    alt="Preview"
                    className="h-32 w-auto object-contain border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setLogoPreview("");
                      setSelectedLogoFile(null);
                      setFormData((prev) => ({
                        ...prev,
                        logo: { ...prev.logo, url: "" },
                      }));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition">
                  <Upload size={18} />
                  <span>Select Logo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                {selectedLogoFile && (
                  <span className="text-sm text-gray-600">
                    {selectedLogoFile.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Logo will be uploaded when you save the partner
              </p>
              <input
                type="text"
                name="logo.alt"
                value={formData.logo.alt}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Logo alt text (optional)"
              />
            </div>
          </div>

          {/* Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website Link (Optional)
            </label>
            <input
              type="url"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description about the partner"
            />
          </div>

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleInputChange}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
            />
            <p className="text-sm text-gray-500 mt-1">
              Lower numbers appear first
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploading}
            >
              {mode === "create" ? "Create Partner" : "Update Partner"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerFormModal;
